import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { LuChevronDown } from 'react-icons/lu';
import GlassIcon from '../../../components/ui/GlassIcon';
import { CARD } from '../../ProfilePage/components/ProfileUi';

/**
 * A page section that opens on demand (its data loads only then): icon,
 * title, one line on what it holds, a badge and a chevron.
 */
const CollapsibleCard = ({ icon: Icon, gradient, title, description, badge, open, onToggle, children }) => {
  const contentId = useId();
  return (
    <section className={`${CARD} tw-overflow-hidden`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={contentId}
        className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-bg-transparent tw-p-4 tw-text-left tw-transition-colors hover:tw-bg-slate-900/[0.02] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-teal-500/50 sm:tw-p-5 dark:hover:tw-bg-white/[0.02]"
      >
        <GlassIcon gradient={gradient} size="xs">
          <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
        <span className="tw-min-w-0 tw-flex-1">
          <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <span className="tw-text-base tw-font-semibold tw-tracking-tight tw-text-slate-900 sm:tw-text-lg dark:tw-text-white">{title}</span>
            {badge}
          </span>
          {description && <span className="tw-mt-0.5 tw-block tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{description}</span>}
        </span>
        <LuChevronDown aria-hidden="true" className={`tw-h-5 tw-w-5 tw-shrink-0 tw-text-slate-400 tw-transition-transform tw-duration-200 ${open ? 'tw-rotate-180' : ''}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={contentId}
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="tw-overflow-hidden"
          >
            <div className="tw-space-y-4 tw-border-t tw-border-slate-200/80 tw-p-4 sm:tw-p-5 dark:tw-border-white/10">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

CollapsibleCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  gradient: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  badge: PropTypes.node,
  open: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export default CollapsibleCard;
